import { useMemo } from 'react';
import { useTranslation } from '@eti/providers';
import { Card, Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { PartOfExternalFlights, TripType } from '@eti/schema-types';
import { WarningCircle } from '@phosphor-icons/react';
import { Notification, variants } from '@eti/components';
import type { BoundType, ExtraProductType, TravelerType } from '../../../models';
import type { TabItemsType } from '../../../../my-bookings/common/Tabs';
import Tabs from '../../../../my-bookings/common/Tabs';
import { getBoundTitle, getBoundType } from '../../../../../widgets/trip-details/utils/format';
import type { BoundBaggageInfoType } from '../baggages/models';
import OrderDetailsUpgradeTripBanner from '../../common/OrderDetailsUpgradeTripBanner';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import { getBaggageGDSInfoPerTraveler } from '../utils';
import { hasDifferencesBetweenBoundsForPassenger } from './utils';
import PassengerBoundInfo from './PassengerBoundInfo';
import PassengerFrequentFlyerCard from './PassengerFrequentFlyerCard';

interface PassengerDrawerProps {
  baggagePerBound: BoundBaggageInfoType[];
  meal?: ExtraProductType | null;
  passenger: TravelerType;
  seat?: ExtraProductType | null;
}

const PassengerDrawer = ({ baggagePerBound, meal, passenger, seat }: PassengerDrawerProps) => {
  const { t } = useTranslation();
  const { addOnOrders } = useOrderDetails();
  const { bounds, journeyType, partOfExternalFlights, shouldShowUpgradeBanner } = useOrderDetails();

  const baggageGDSInfoPerTraveler = useMemo(
    () => getBaggageGDSInfoPerTraveler(addOnOrders),
    [addOnOrders],
  );

  const hasBaggageDifferences = hasDifferencesBetweenBoundsForPassenger(
    passenger.id || '',
    baggagePerBound,
  );

  const tabItems = useMemo(
    () =>
      bounds.reduce((acc: TabItemsType[], bound: BoundType, boundIndex) => {
        if (passenger.id) {
          const isReturnBound = journeyType === TripType.Return && boundIndex === 1;

          const boundTitle = getBoundTitle(
            getBoundType(boundIndex),
            journeyType === TripType.MultiStop,
          );

          const shouldShowWarningIconInTab = isReturnBound && hasBaggageDifferences;

          acc.push({
            title: (
              <Inline align="center" alignY="center" noWrap spacing={4}>
                {shouldShowWarningIconInTab && (
                  <WarningCircle
                    aria-labelledby="baggage-differences-notification"
                    color={vars.colors.signal.caution.light}
                    data-testid={`passenger-drawer-${bound.boundId}-warning-icon`}
                    size={20}
                    weight="fill"
                  />
                )}
                {t(boundTitle, boundIndex + 1)}
              </Inline>
            ),
            content: (
              <PassengerBoundInfo
                bound={bound}
                boundBaggage={baggagePerBound[boundIndex]}
                boundIndex={boundIndex}
                isReturnBound={isReturnBound}
                meal={meal}
                passenger={passenger}
                seat={seat}
              />
            ),
          });
        }
        return acc;
      }, []),
    [baggagePerBound, bounds, hasBaggageDifferences, journeyType, meal, passenger, seat, t],
  );

  return (
    <Stack>
      {partOfExternalFlights === PartOfExternalFlights.All && (
        <Notification dataTestId="ryanair-notification">
          <Text asHtml>{t('OrderDetails.RyanairFlight.Baggage.Drawer.Text')}</Text>
        </Notification>
      )}
      {hasBaggageDifferences && (
        <Notification
          contentId="baggage-differences-notification"
          dataTestId="baggage-differences-notification"
          variant={variants.WARNING}
        >
          {t('OrderDetails.Baggage.Drawer.DifferencesNotification')}
        </Notification>
      )}
      <Card data-testid="passenger-drawer" padding={journeyType === TripType.OneWay ? 16 : 0}>
        {journeyType === TripType.OneWay ? (
          <PassengerBoundInfo
            bound={bounds[0]}
            boundBaggage={baggagePerBound[0]}
            boundIndex={0}
            meal={meal}
            passenger={passenger}
            seat={seat}
          />
        ) : (
          <Tabs id="passenger-bounds" tabItems={tabItems} />
        )}
      </Card>
      {passenger.id && baggageGDSInfoPerTraveler[passenger.id] && (
        <Card key={passenger.id}>
          <Stack>
            <Heading data-testid="baggage-gds-card-title" level={4}>
              {t('OrderDetails.BaggageGDS.Title')}
            </Heading>
            <Stack data-testid="baggage-gds-card-agent-notes" spacing={0}>
              {baggageGDSInfoPerTraveler[passenger.id].map((agentNote, index) => (
                <Text
                  data-testid={`note-${passenger.id}${index}`}
                  key={`note-${passenger.id}${index}`}
                >
                  {agentNote}
                </Text>
              ))}
            </Stack>
          </Stack>
        </Card>
      )}
      {passenger.frequentFlyerCard && (
        <PassengerFrequentFlyerCard frequentFlyerCard={passenger.frequentFlyerCard} />
      )}
      {shouldShowUpgradeBanner && (
        <OrderDetailsUpgradeTripBanner
          dataTestId="passenger-drawer"
          headingAs="h3"
          shouldDisplayInline
        />
      )}
    </Stack>
  );
};

export default PassengerDrawer;
